import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { HTTPCONSTANTS } from './http-constants';
import { environment } from 'src/environments/environment';
import { environment as _environemnt } from 'src/app/environment';
import { site } from '../site-config';

/** All API base URLs come from environment. Canonical base is api.platex.co.uk (mapped to API via CloudFront → EC2). */
const LEGACY_API_ORIGIN = 'https://api.platex.co.uk/api';
const LEGACY_API_ORIGIN_HTTP = 'http://api.platex.co.uk/api';
const CLOUDFRONT_API_BASE = 'https://d3j6eu5y3tsx1j.cloudfront.net/api';

// Staging (and unknown hosts) must never reach a production backend. Every
// HttpClient request is checked AFTER placeholder rewriting; a production
// host fails as a network error instead of being sent.
const PRODUCTION_HOSTS = ['api.platex.co.uk', 'plato.platex.co.uk', 'www.platex.co.uk', 'platex.co.uk', 'd3j6eu5y3tsx1j.cloudfront.net'];
// The read-only Names API (static daily artifact, no writes) is the one API
// Gateway host staging may call directly.
const NAMES_API_HOST = '13dfkzcsnh.execute-api.eu-west-2.amazonaws.com';
function blockedOffProduction(url: string): boolean {
  let host = '';
  try {
    host = new URL(url, window.location.href).hostname.toLowerCase();
  } catch {
    return true;
  }
  // Production search and every other API Gateway endpoint: staging reaches
  // search only through its own /search-api proxy.
  if (host === NAMES_API_HOST) return false;
  return PRODUCTION_HOSTS.includes(host) || host.endsWith('.execute-api.eu-west-2.amazonaws.com') || host.endsWith('.invalid');
}

@Injectable()
export class UrlInterceptor implements HttpInterceptor {
  private useLocalAPI: boolean = true;
  private useLocalPlato: boolean = false;

  constructor() { }
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (site.name !== 'staging' && site.name !== 'unknown') return this.route(request, next);
    const guarded: HttpHandler = {
      handle: (req: HttpRequest<any>) =>
        blockedOffProduction(req.url)
          ? throwError(new HttpErrorResponse({ status: 0, url: req.url, statusText: 'Blocked: production host from ' + site.name }))
          : next.handle(req),
    };
    return this.route(request, guarded);
  }

  private route(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let url = request.url;

    // Ensure all API calls use the canonical base (api.platex.co.uk); rewrite legacy or CloudFront URLs to it
    const apiBase = environment.apiProtocol + environment.apiBaseAddress;
    if (url.indexOf('api.platex.co.uk') !== -1) {
      url = url.replace(LEGACY_API_ORIGIN, apiBase).replace(LEGACY_API_ORIGIN_HTTP, apiBase);
      return next.handle(request.clone({ url }));
    }
    if (url.indexOf('d3j6eu5y3tsx1j.cloudfront.net') !== -1) {
      url = url.replace(CLOUDFRONT_API_BASE, apiBase);
      return next.handle(request.clone({ url }));
    }

    if (request.url.indexOf(HTTPCONSTANTS.dataApiBaseAddress) > -1) {
      // DATA API (MarketWatch, DVLAAuction) → CloudFront/EC2 – Top Auction Results, Live Feed, DVLA search
      var protocol = environment.dataApiProtocol;
      var baseAddress = environment.dataApiBaseAddress;
      request = request.clone({
        url: request.url
          .replace(HTTPCONSTANTS.dataApiProtocol, protocol)
          .replace(HTTPCONSTANTS.dataApiBaseAddress, baseAddress),
      });
    } else if (request.url.indexOf(HTTPCONSTANTS.apiBaseAddress) > -1) {
      // MAIN API (auth, user, listings, platedetails, blog, etc.) → CloudFront/EC2
      var protocol = environment.apiProtocol;
      var baseAddress = environment.apiBaseAddress;
      if (!environment.production && this.useLocalAPI) {
        protocol = environment.local_apiProtocol;
        baseAddress = environment.local_apiBaseAddress;
      }
      request = request.clone({
        url: request.url
          .replace(HTTPCONSTANTS.protocol, protocol)
          .replace(HTTPCONSTANTS.apiBaseAddress, baseAddress),
      });
    } else if (request.url.indexOf(HTTPCONSTANTS.platoBaseAddress) > -1) {
      // PLATEX PLATO REQUEST
      var protocol = environment.platoProtocol;
      var baseAddress = environment.platoBaseAddress;
      if (!environment.production && this.useLocalPlato) {
        protocol = environment.local_platoProtocol;
        baseAddress = environment.local_platoBaseAddress;
      }
      request = request.clone({
        url: request.url
          .replace(HTTPCONSTANTS.platoProtocol, protocol)
          .replace(HTTPCONSTANTS.platoBaseAddress, baseAddress),
      });
    } else if (request.url.indexOf(HTTPCONSTANTS.editorialApiBaseAddress) > -1) {
      // EDITORIAL INTELLIGENCE (Dev2 Plato FastAPI/Lambda HttpApi). Dev2-only PoC;
      // the same isolated host is used in dev and prod builds. Do NOT append the
      // plato-search '&environment=dev' suffix here.
      request = request.clone({
        url: request.url
          .replace(HTTPCONSTANTS.editorialApiProtocol, environment.editorialApiProtocol)
          .replace(HTTPCONSTANTS.editorialApiBaseAddress, environment.editorialApiBaseAddress),
      });
    } else if (request.url.indexOf(HTTPCONSTANTS.mode2_base) > -1) {
      // MODE 2 PRODUCTION SEARCH — direct call to the production Mode 2 Lambda.
      // Used as the primary search path when environment.useLiveV2 is true.
      // Falls back gracefully if mode2BaseAddress is empty.
      const m2Protocol = (environment as any).mode2Protocol || 'https://';
      const m2Base = (environment as any).mode2BaseAddress || '';
      if (!m2Base) {
        // No production Mode 2 endpoint configured — return empty response to
        // let the caller fall back to legacy.
        return next.handle(request.clone({ url: '' }));
      }
      request = request.clone({
        url: request.url
          .replace(HTTPCONSTANTS.mode2_protocol, m2Protocol)
          .replace(HTTPCONSTANTS.mode2_base, m2Base),
      });
    } else if (request.url.indexOf(HTTPCONSTANTS.shadow_log_base) > -1) {
      // SHADOW LOG — POST/PATCH comparison records to the shadow log Lambda.
      const slProtocol = (environment as any).shadowLogProtocol || 'https://';
      const slBase = (environment as any).shadowLogBaseAddress || '';
      request = request.clone({
        url: request.url
          .replace(HTTPCONSTANTS.shadow_log_protocol, slProtocol)
          .replace(HTTPCONSTANTS.shadow_log_base, slBase),
      });
    } else if (request.url.indexOf(HTTPCONSTANTS.names_register_base) > -1) {
      // NAMES REGISTER / RESOLVE base resolution, in priority order:
      //   1. local ng serve   -> environment.namesRegisterBaseAddress (local artifact server)
      //   2. DEV2 host (runtime hostname check) -> the ISOLATED dev2-only Names
      //      stack (plato-names-dev2). This keeps ONLY dev2 on the dev2 stack.
      //   3. anything else (prod) -> plato-search canonical (harmless until the
      //      Names processes ship there; never points prod at dev2 infra).
      const isDev2Host = site.name === 'dev2';
      const localBase = (environment as any).namesRegisterBaseAddress;
      const dev2Base = (environment as any).namesRegisterDev2BaseAddress;
      let nrProtocol: string;
      let nrBase: string;
      if (!environment.production && localBase) {
        nrProtocol = (environment as any).namesRegisterProtocol || 'http://';
        nrBase = localBase;
      } else if (dev2Base && (isDev2Host || site.name === 'staging' || site.name === 'production')) {
        // dev2, staging and www all read the Names API (site-config sets it
        // for the release hosts; unknown hosts keep it blank and blocked).
        nrProtocol = 'https://';
        nrBase = dev2Base;
      } else {
        nrProtocol = environment.platoSearchCanonicalProtocol;
        nrBase = environment.platoSearchCanonicalBaseAddress;
      }
      request = request.clone({
        url: request.url
          .replace(HTTPCONSTANTS.names_register_protocol, nrProtocol)
          .replace(HTTPCONSTANTS.names_register_base, nrBase),
      });
    } else if (request.url.indexOf(HTTPCONSTANTS.plato_search_canonical_base) > -1) {
      // CANONICAL PLATEX PLATO SEARCH REQUEST
      // Resolves directly from environment.platoSearchCanonicalBaseAddress,
      // which is NOT subject to the platex-dev2 Mode 2 sed rewrite. Used for
      // endpoints (new-release, etc.) Mode 2 doesn't implement.
      var canonicalProtocol = environment.platoSearchCanonicalProtocol;
      var canonicalBase = environment.platoSearchCanonicalBaseAddress;
      var canonicalEnv = _environemnt.dev() ? '&environment=dev' : '';
      request = request.clone({
        url:
          request.url
            .replace(HTTPCONSTANTS.plato_search_canonical_protocol, canonicalProtocol)
            .replace(HTTPCONSTANTS.plato_search_canonical_base, canonicalBase) + canonicalEnv,
      });
    } else {
      // PLATEX PLATO SEARCH REQUEST (default — on platex-dev2 this is rewritten
      // to Mode 2 at Amplify build time by the branch buildSpec sed)
      var protocol = environment.platoSearchProtocol;
      var baseAddress = environment.platoSearchBaseAddress;
      var env = _environemnt.dev() ? '&environment=dev' : '';
      request = request.clone({
        url:
          request.url
            .replace(HTTPCONSTANTS.plato_search_protocol, protocol)
            .replace(HTTPCONSTANTS.plato_search_base, baseAddress) + env,
      });
    }
    return next.handle(request);
  }
}
